import { ChangeEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
//
import { Button, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Iconify } from '@/components';
import { VBMCardTitle, VBMCardWrapper, VBMEditButton } from '..';
// hooks + utils
import useResponsive from '@/hooks/useResponsive';
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
// types
import { BookingSession } from '@/features/bookings-management/types';
type VBMDetailCardProps = {
  title: string; // Hours | Bedrooms | Bathrooms
  icon: string;
  hours: number;
  iconSize?: number;
  editable?: boolean;
  bookingSession?: BookingSession;
};

export default function VBMDetailCard({
  title,
  icon,
  hours,
  iconSize = 30,
  editable = false,
  bookingSession,
}: VBMDetailCardProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [inputValue, setInputValue] = useState<string | number>(hours);
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('sessionID');
  const downMd = useResponsive('down', 'md');
  const betweenMdLg = useResponsive('between', undefined, 'md', 'lg');

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setInputValue(Number(value));
  };

  const handleToggleEdit = (value: boolean) => setIsEdit(value);

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: () => {
      handleToggleEdit(false);
    },
    successText: `Number of ${title.toLowerCase()} updated`,
  });

  const handleSaveChanges = () => {
    sessionUpdateMutation.mutate({
      bookingSessionId: Number(sessionID),
      bookingSessionDto: {
        [title.toLowerCase()]: inputValue,
        booking_cleaners:
          bookingSession?.booking_session_cleaners?.map(
            (cleaner) => cleaner.id
          ) ?? [],
      },
    });
  };

  const handleCancel = () => {
    setInputValue(hours);
    handleToggleEdit(false);
  };

  const cardTitle = `${hours} ${hours !== 1 ? title : title.slice(0, -1)}`;
  return (
    <VBMCardWrapper>
      {!isEdit && editable && (
        <VBMEditButton onClickButton={() => setIsEdit(true)} />
      )}

      <Stack
        alignItems="center"
        justifyContent="center"
        direction="row"
        sx={{ width: '100%', pt: 0.5 }}
        spacing={downMd ? 1 : 2}
      >
        {!betweenMdLg && <Iconify icon={icon} width={downMd ? 24 : iconSize} />}

        {isEdit ? (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                name={title.toLowerCase()}
                value={inputValue}
                onChange={handleInputChange}
                size="small"
                type="number"
                inputProps={{ step: 0.5 }}
                sx={{
                  background: (theme) => theme.palette.background.paper,
                  width: 75,
                }}
              />
              <VBMCardTitle title={title} sx={{ textAlign: 'center' }} />
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                onClick={handleSaveChanges}
                loading={sessionUpdateMutation.isLoading}
              >
                {downMd ? 'Save' : 'Save changes'}
              </LoadingButton>
            </Stack>
          </Stack>
        ) : (
          <VBMCardTitle title={cardTitle} sx={{ textAlign: 'center' }} />
        )}
      </Stack>
    </VBMCardWrapper>
  );
}
